import { orderBy, cloneDeep, remove } from 'lodash-es';
import { IBookingInfoAtTimeExtended } from './type';
import moment from 'moment';
import DateUtilsService from 'external-services/date-utils-service';
import {
  BookingMethods,
  BookingStatusNames,
  BookingType,
  IBooking,
  IBookingTime,
  IDiarySchedule,
  IDiaryService,
  IInfoAtTime,
  ISectionLookupCache,
  ITableDate,
  ITableItem,
  JOIN_PREFIX,
  StatusUnconfirmed,
  TimeStatus,
} from 'shared-types/index';
import ObjectCopyService from 'external-services/object-copy-service/index';
import TableSelectionService from 'external-services/table-selection-service/index';
import SectionLookupService from 'external-services/section-lookup-service/sectionLookup.service';
import { BookingAvailabilityService } from 'external-services/booking-availability-service';
import { IBookingInfoAtTime } from 'external-services/booking-availability-service/types';

const NS = 'TimeSelectionService';

class TimeSelectionService {
  public selectNearestTime(
    timeToSelect: Date,
    bookingDate: Date,
    bookingTimes: IInfoAtTime[]
  ): IInfoAtTime {
    if (!timeToSelect) {
      return null;
    }
    this.enforceCurrentDay(timeToSelect, bookingDate);
    // a time has been passed it select the closet interval to that time
    if (bookingTimes.length === 1) {
      // @todo: quick hot fix for services with only 1 time. Need to clean this logic up a bit though. severity: medium
      return bookingTimes[0];
    } else {
      // uses a for loop so it can check previous index
      for (let i = 1; i < bookingTimes.length; i++) {
        const timeBefore: IInfoAtTime = bookingTimes[i - 1];
        const timeAfter: IInfoAtTime = bookingTimes[i];

        if (DateUtilsService.isDateSame(timeToSelect, timeAfter.time)) {
          return timeAfter;
        }

        if (DateUtilsService.isBeforeDate(timeToSelect, timeAfter.time)) {
          const diffToBefore: number = DateUtilsService.getDifferenceInMS(
            timeToSelect,
            timeBefore.time
          );
          const diffToAfter: number = DateUtilsService.getDifferenceInMS(
            timeToSelect,
            timeAfter.time
          );
          return diffToBefore < diffToAfter ? timeBefore : timeAfter;
        }
        if (i === bookingTimes.length - 1) {
          return timeAfter;
        }
      }
    }
    return null;
  }

  // This funciton get nearest future bookingTime
  // require: bookingTimes are sorted
  public selectNearestFutureTime(
    timeToSelect: Date,
    bookingDate: Date,
    bookingTimes: IInfoAtTime[],
    includeSameTime = false
  ): IInfoAtTime {
    if (!timeToSelect) {
      return null;
    }

    this.enforceCurrentDay(timeToSelect, bookingDate);
    for (let i = 0; i < bookingTimes.length; i++) {
      const infoAtTime: IInfoAtTime = bookingTimes[i];

      if (includeSameTime && DateUtilsService.isDateSame(timeToSelect, bookingDate, true)) {
        return infoAtTime;
      }

      if (DateUtilsService.isBeforeDate(timeToSelect, infoAtTime.time)) {
        return infoAtTime
      }
    }
    return null;
  }

  // forces time past midnight to be part of the current day, not the next day
  private enforceCurrentDay(dateWithVenueTime: Date, scheduleDay: Date): void {
    dateWithVenueTime.setDate(scheduleDay.getDate());
    dateWithVenueTime.setMonth(scheduleDay.getMonth());
    dateWithVenueTime.setFullYear(scheduleDay.getFullYear());
  }

  /**
   * Similar to selectTime in Diary
   * @param props
   */
  public async addTableToSelectedInfoAtTime(props: {
    selectedTime: Date; // same as IInfoAtTime, but has table
    selectedInfoAtTime: IBookingTime; // same as IInfoAtTime, but has table
    autoSelectTable: boolean;

    activeBookingMethod: BookingMethods;
    activeBookingType: BookingType;
    activePax: number;
    tablesList: ITableItem[];
    tableIds: string[];
    isCustomTime: boolean;
    isEditBooking: boolean;
    hasUserDefinedTables?: boolean
    // getSelectedInfoAtTimeWithUpdatedAvailability: (
    //   selectedInfoAtTime: IBookingTime
    // ) => IBookingTime;
  }) {
    let tables: ITableItem[] = [];
    let availableTablesList: ITableItem[];

    // let newAvailableTime: IBookingTime = cloneDeep(props.selectedInfoAtTime);
    const newSelectedTime: IBookingTime = cloneDeep(props.selectedInfoAtTime);
    const previouslySelectedInfoAtTime: IBookingTime = cloneDeep(props.selectedInfoAtTime);
    let newSelectedTableIds: string[];

    // don't need this 'state', as it was only used for CSS class in old Diary
    // if (newSelectedTime) {
    //   newSelectedTime.state = '';
    // }

    if (props.activeBookingMethod === BookingMethods.walkin && props.isCustomTime) {
      /**
       * @todo: review when doing walkin
       */
      // const originTime = cloneDeep(newSelectedTime.time);
      // newAvailableTime = {
      //   ...newSelectedTime,
      //   time: props.selectedTime,
      // };
      // (newSelectedTime as any).originTime = originTime;
    }

    // newSelectedTime = cloneDeep(availableTime as any);
    // newLastSelectedTime = cloneDeep(newSelectedTime);

    let selectedInfoAtTime: IBookingTime = props.selectedInfoAtTime;

    // if there are table joins available at this time, the objects in vaidJoinsAtTime need to be mutable, so the ids can get set
    if (
      !Object.isExtensible(props.selectedInfoAtTime) ||
      props.selectedInfoAtTime.vaidJoinsAtTime?.length
    ) {
      selectedInfoAtTime = cloneDeep(props.selectedInfoAtTime);
    }

    let sectionLookup: ISectionLookupCache = null;
    if (props.isEditBooking) {
      // selectedInfoAtTime = props.getSelectedInfoAtTimeWithUpdatedAvailability(selectedInfoAtTime);
      //
      // console.log(NS, 'selectedInfoAtTime', selectedInfoAtTime);

      sectionLookup = (await SectionLookupService.getSectionsLookUp()) as ISectionLookupCache;
    }

    /**
     * @todo: freeTablesAtTime and vaildTablesAtTime should not be including current table.
     * Check BookingAvailablilityService implementations
     * maybe BookingAvailabilityService.checkBookingsAtTime is missing?
     */
    const { tableSelectionListWithJoins, tableSelectionList, tableStatusLookUps } =
      TableSelectionService.createTableSectionList(
        selectedInfoAtTime,
        props.tablesList,
        sectionLookup
      );
    const newSelectedTablesList: ITableItem[] = tableSelectionListWithJoins;
    
    // if user has manually chosen a table or table join, here we try to maintain that selection
    if (props.hasUserDefinedTables && props.tableIds.length) {
      
      // if there are multiple tables, check if the tables are a table join
      if (props.tableIds.length > 1 ) {
        const tableJoins = newSelectedTablesList.filter(t => t._id.indexOf(JOIN_PREFIX) === 0);
        const tableJoinsMatch = tableJoins.find(t => {
          return t.tables.length === props.tableIds.length 
            && t.tables.every(tId => props.tableIds.includes(tId))
        });
        if (tableJoinsMatch) {
          tables = [tableJoinsMatch];
        }
      } else {
        // if a single table, check that table is in the list
        tables = newSelectedTablesList.filter(i => props.tableIds.includes(i._id))
      }
    }

    // if user has not manually chosen a table, or there are no tables selected, try to find a table that fits with the selected time and pax
    if (!tables?.length || !props.hasUserDefinedTables) {
      // select the table with the lowest priority and capacity
      const table = TableSelectionService.getTableByCapacityAndPriority(
        selectedInfoAtTime.vaildTablesAtTime?.length? selectedInfoAtTime.vaildTablesAtTime : selectedInfoAtTime.vaidJoinsAtTime,
        props.activePax
      );
      tables = table ? [table] : []
    }
    newSelectedTime.table = tables[0];
    if (tables.length) {
      // update the booking with the table
      if (props.autoSelectTable) {
        // const hasTableJoin: boolean = table.tablesList?.length > 1;
        // availableTablesList = hasTableJoin ? table.tablesList : [table];
        availableTablesList = tables
      }

      // newSelectedTableIds = newActiveBooking.tables.map(function (table) {
      //   return table._id;
      // });
    } else {
      // update the booking with the table
      availableTablesList = [];
      // newSelectedTableIds = [];
    }

    // set the booking time to the selected time
    // newActiveBooking.time = availableTime.time;

    // set the availableTime to selected
    // newAvailableTime.state = 'selected';

    if (newSelectedTime.status === TimeStatus.unavailable) {
      /**
       * @todo: need to implement standby list defaults
       */
      // setStandbyDefaults();
      // newActiveBooking = this.setStandbyDefaults(newActiveBooking);
    }

    return {
      newSelectedTime,
      previouslySelectedInfoAtTime,
      newSelectedTablesList,
      availableTablesList,
    };
  }

  public getNowVenueTime(today: Date, timeZoneId: string): Date {
    const date = cloneDeep(today);
    const now = DateUtilsService.getVenueTime(timeZoneId);
    date.setHours(now.getHours());
    date.setMinutes(now.getMinutes());
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
  }

  public setStandbyDefaults(activeBooking: IBooking) {
    const newActiveBooking = cloneDeep(activeBooking);
    if (newActiveBooking.bookingType === BookingType.StandbyList) {
      newActiveBooking.status = StatusUnconfirmed;
      // bookingNewCtrl.statusName = BookingStatusNames.Unconfirmed;
      // bookingNewCtrl.customer.subscribed = false;
    }
    return newActiveBooking;
  }

  public updateBookingTimes(
    time: IBookingInfoAtTimeExtended,
    bookingTimes: IBookingInfoAtTimeExtended[]
  ) {
    if (time && (time as any).originTime)
      for (let i = 1; i < bookingTimes.length; i++) {
        const comp: IBookingInfoAtTimeExtended = bookingTimes[i];
        if ((time as any).originTime.getTime() === comp.time.getTime()) {
          delete (time as any).originTime;
          bookingTimes[i] = time;
          break;
        }
      }
    return bookingTimes;
  }

  public timeExists(selectedInfoAtTime: IBookingTime, availableTimes: {time: Date}[]): boolean {
    if (!selectedInfoAtTime || !availableTimes?.length) {
      return false;
    }
    // const foundMatch = availableTimes.some((avTm) => {
    //   return moment(selectedInfoAtTime.time).isSame(avTm.time);
    // });

    // supports custom times
    // the selected time is in the list of available times, also includes the last one on the list
    return DateUtilsService.isOnOrInBetweenDates(
      selectedInfoAtTime.time,
      availableTimes[0].time,
      availableTimes[availableTimes.length - 1].time
    );
  }
}

const instance = new TimeSelectionService();
export default instance as TimeSelectionService;
